import sidekickLogoBlue from "@assets/images/sidekick-logo-blue.png";
import sidekickLogoWhite from "@assets/images/sidekick-logo-white.png";
import radarLogoBlue from "@assets/images/radar-by-sidekick-logo-blue.png";
import radarLogoWhite from "@assets/images/radar-by-sidekick-logo-white.png";
import smallRoundedSidekickLogoBlue from "@assets/images/small-rounded-sidekick-logo-blue.png";
import smallRoundedRadarLogoBlue from "@assets/images/small-rounded-radar-logo-blue.png";
import {
  DomainGroup,
  domainGroupEnum,
  getOtherDomainGroup,
} from "@shared/utils/domain-group";
import { APP_NAME } from "./useAppName";
import { useDomainGroup } from "./useDomainGroup";

type AppLogoVariant = "blue" | "white" | "smallRoundedBlue";

const APP_LOGO_ASSET: Record<AppLogoVariant, Record<DomainGroup, string>> = {
  blue: {
    [domainGroupEnum.SIDEKICK]: sidekickLogoBlue,
    [domainGroupEnum.RADAR]: radarLogoBlue,
  },
  white: {
    [domainGroupEnum.SIDEKICK]: sidekickLogoWhite,
    [domainGroupEnum.RADAR]: radarLogoWhite,
  },
  smallRoundedBlue: {
    [domainGroupEnum.SIDEKICK]: smallRoundedSidekickLogoBlue,
    [domainGroupEnum.RADAR]: smallRoundedRadarLogoBlue,
  },
};

const APP_LOGO_HEIGHT_PX = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
} as const;

type AppLogoSize = keyof typeof APP_LOGO_HEIGHT_PX;

// Sidekick and Radar never share a logo height by accident — every context
// either takes this default pair or an explicit per-domain override (e.g.
// { sidekick: "lg", radar: "xl" } on the login screen), never a single size
// silently reused for both brands' regular logo.
const DEFAULT_LOGO_SIZE_BY_DOMAIN_GROUP: Record<DomainGroup, AppLogoSize> = {
  [domainGroupEnum.SIDEKICK]: "sm",
  [domainGroupEnum.RADAR]: "md",
};

export type AppLogoSizeOverride =
  | AppLogoSize
  | Partial<Record<DomainGroup, AppLogoSize>>;

const getAppLogoAssetForDomainGroup = (
  domainGroup: DomainGroup,
  variant: AppLogoVariant,
  size?: AppLogoSizeOverride,
) => {
  const resolvedSize =
    (typeof size === "object" ? size[domainGroup] : size) ??
    DEFAULT_LOGO_SIZE_BY_DOMAIN_GROUP[domainGroup];

  return {
    src: APP_LOGO_ASSET[variant][domainGroup],
    alt: APP_NAME[domainGroup],
    height: `${APP_LOGO_HEIGHT_PX[resolvedSize]}px`,
    isRadarDomain: domainGroup === domainGroupEnum.RADAR,
  };
};

export const useAppLogoAsset = (
  variant: AppLogoVariant = "blue",
  size?: AppLogoSizeOverride,
) => getAppLogoAssetForDomainGroup(useDomainGroup(), variant, size);

export const useOtherAppLogoAsset = (
  variant: AppLogoVariant = "blue",
  size?: AppLogoSizeOverride,
) =>
  getAppLogoAssetForDomainGroup(
    getOtherDomainGroup(useDomainGroup()),
    variant,
    size,
  );
